'use client';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  TextField,
  Typography,
  Checkbox,
  FormControlLabel,
  IconButton,
  Switch,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import { useSession } from 'next-auth/react';
import { useConnectedAccounts } from '@/hooks/useConnectedAccounts';
import { usePublishDialogChrome } from '@/hooks/usePublishDialogChrome';
import type { PublishV1Platform } from '@/lib/publish/publishScope';
import { PUBLISH_V1_PAYLOAD_HINTS } from '@/lib/publish/publishScope';
import {
  canAttachImageToThreads,
  canPublishImageToInstagram,
  isPublicHttpsMediaUrl,
  threadsUtf8ByteLength,
  toAbsoluteMediaUrl,
} from '@/lib/publish/mediaPublishEligibility';
import { composePublishPlainText } from '@/lib/publish/htmlToPublishText';
import { reportNativePublishDiagnostics } from '@/lib/nativePublishDiagnosticsClient';
import {
  postFacebookPublish,
  postInstagramPublish,
  postLinkedinPublish,
  postMastodonPublish,
  postPinterestPublish,
  postRedditPublish,
  postThreadsPublish,
  postTwitterPublish,
  postYoutubePublish,
  postTiktokPublish,
  postVimeoPublish,
  postDribbblePublish,
  postSnapchatPublish,
  postDiscordPublish,
  postSlackPublish,
  type PublishApiResult,
} from '@/lib/publish/publishApiClient';
import {
  buildFacebookNativePublishBody,
  buildInstagramNativePublishBody,
  buildLinkedinNativePublishBody,
  buildMastodonNativePublishBody,
  buildPinterestNativePublishBody,
  buildRedditNativePublishBody,
  buildThreadsNativePublishBody,
  buildTwitterNativePublishBody,
  buildTwitterThreadPublishBody,
  buildYoutubeNativePublishBody,
  buildTikTokNativePublishBody,
  buildVimeoNativePublishBody,
  buildDribbbleNativePublishBody,
  buildSnapchatNativePublishBody,
  buildDiscordNativePublishBody,
  buildSlackNativePublishBody,
} from '@/lib/publish/publishRequestBodies';
import { computePublishEligibility } from '@/lib/publish/publishEligibility';
import { useVisualViewportKeyboardInset } from '@/hooks/useVisualViewportKeyboardInset';
import { PublishMultiResults } from '@/components/publish/PublishMultiResults';
import { PublishOutcomeBanner } from '@/components/publish/PublishOutcomeBanner';
import { summarizePublishResults } from '@/lib/publish/summarizePublishResults';
import { ThreadsCaptionByteCounter } from '@/components/publish/ThreadsCaptionByteCounter';
import type { PublishableMediaShape } from '@/lib/publish/publishableMediaShape';
import { PublishPlatformSelector, getDefaultPublishSelection } from '@/components/publish/PublishPlatformSelector';
import type { MediaPublishDialogMedia } from '@/components/publish/MediaPublishDialog';
import {
  emptyTikTokPublishOptions,
  TikTokPublishOptionsPanel,
  type TikTokPublishOptionsValue,
} from '@/components/publish/TikTokPublishOptionsPanel';
import {
  readPublishDialogCompact,
  writePublishDialogCompact,
} from '@/lib/publish/publishDialogUxPrefs';
import { PUBLISH_DIALOG_SELECT_MENU_PROPS } from '@/lib/publish/publishDialogSelectMenuProps';
import { startOAuthConnect } from '@/lib/publish/startOAuthConnect';

const NO_ASSET_MEDIA: PublishableMediaShape = { url: '', type: 'image/jpeg' };

export type CreateComposerPublishDialogProps = {
  open: boolean;
  onClose: () => void;
  contentTitle: string;
  editorHtml: string;
  /** Library image or video for networks that need a URL (must be public HTTPS where required). */
  publishImageMedia: MediaPublishDialogMedia | null;
  onPublishImageMediaChange: (media: MediaPublishDialogMedia | null) => void;
  onRequestPickImage: () => void;
};

export function CreateComposerPublishDialog({
  open,
  onClose,
  contentTitle,
  editorHtml,
  publishImageMedia,
  onPublishImageMediaChange,
  onRequestPickImage,
}: CreateComposerPublishDialogProps) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const compactResults = useMediaQuery(theme.breakpoints.down('sm'));
  const showThreadsByteChip = useMediaQuery(theme.breakpoints.down('sm'));
  const keyboardBottomInset = useVisualViewportKeyboardInset();
  const { status, data: session } = useSession();
  const accountsEnabled = status === 'authenticated';
  const userPlan = session?.user?.plan ?? 'FREE';
  const { data: accounts, isPending: accountsPending } = useConnectedAccounts(accountsEnabled);

  const [caption, setCaption] = useState('');
  const [selected, setSelected] = useState<Set<PublishV1Platform>>(() => new Set());
  const [facebookPageId, setFacebookPageId] = useState('');
  const [fbPages, setFbPages] = useState<Array<{ id: string; name: string }>>([]);
  const [fbPagesLoading, setFbPagesLoading] = useState(false);
  const [youtubeTitle, setYoutubeTitle] = useState('');
  const [youtubePrivacy, setYoutubePrivacy] = useState<'public' | 'unlisted' | 'private'>('public');
  const [youtubeShorts, setYoutubeShorts] = useState(false);
  const [tiktokOptions, setTiktokOptions] = useState<TikTokPublishOptionsValue>(emptyTikTokPublishOptions);
  const [tiktokOptionsValid, setTiktokOptionsValid] = useState(false);
  const [twitterThreadExtras, setTwitterThreadExtras] = useState<string[]>([]);
  const [pinterestBoardId, setPinterestBoardId] = useState('');
  const [pinterestBoards, setPinterestBoards] = useState<Array<{ id: string; name: string }>>([]);
  const [redditSubreddit, setRedditSubreddit] = useState('');
  const [slackChannelId, setSlackChannelId] = useState('');
  const [slackChannels, setSlackChannels] = useState<Array<{ id: string; name: string }>>([]);
  const [discordChannelId, setDiscordChannelId] = useState('');
  const [discordChannels, setDiscordChannels] = useState<Array<{ id: string; name: string }>>([]);
  const [publishing, setPublishing] = useState(false);
  const [results, setResults] = useState<PublishApiResult[] | null>(null);
  const publishResultsRef = useRef<HTMLDivElement | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const [compactView, setCompactView] = useState(false);

  usePublishDialogChrome(open);

  useEffect(() => {
    if (open) setCompactView(readPublishDialogCompact());
  }, [open]);

  const setCompactViewPersisted = useCallback((next: boolean) => {
    setCompactView(next);
    writePublishDialogCompact(next);
  }, []);
  const initKeyRef = useRef<string | null>(null);

  const mediaForSelector: PublishableMediaShape = publishImageMedia ?? NO_ASSET_MEDIA;

  useEffect(() => {
    if (!open) {
      initKeyRef.current = null;
      return;
    }
    const key = `${contentTitle.length}:${editorHtml.length}:${publishImageMedia?.id ?? 'none'}:${accounts ? 'a' : 'p'}`;
    if (initKeyRef.current !== key) {
      initKeyRef.current = key;
      const nextCaption = composePublishPlainText(contentTitle, editorHtml);
      setCaption(nextCaption);
      setFacebookPageId('');
      setFbPages([]);
      const ytDefault = (contentTitle.trim() || nextCaption.trim()).slice(0, 100);
      setYoutubeTitle(ytDefault);
      setYoutubePrivacy('public');
      setYoutubeShorts(false);
      setTiktokOptions(emptyTikTokPublishOptions());
      setTiktokOptionsValid(false);
      setTwitterThreadExtras([]);
      setPinterestBoardId('');
      setPinterestBoards([]);
      setRedditSubreddit('');
      setSlackChannelId('');
      setSlackChannels([]);
      setDiscordChannelId('');
      setDiscordChannels([]);
      setResults(null);
      setSelected(
        getDefaultPublishSelection(
          accounts,
          mediaForSelector,
          nextCaption,
          {
            facebookPageId: '',
            youtubeTitle: ytDefault,
            pinterestBoardId: '',
            redditSubreddit: '',
            slackChannelId: '',
            discordChannelId: '',
          },
          userPlan,
        ),
      );
    }
  }, [open, contentTitle, editorHtml, publishImageMedia, accounts, mediaForSelector, userPlan]);

  useEffect(() => {
    const fbConnected = Boolean(accounts?.facebook?.isConnected);
    if (!open || !fbConnected) {
      if (!open) {
        setFbPages([]);
        setFacebookPageId('');
        setFbPagesLoading(false);
      }
      return;
    }
    let cancelled = false;
    setFbPagesLoading(true);
    void fetch('/api/facebook/pages', { credentials: 'include' })
      .then((r) => r.json())
      .then((j: { pages?: Array<{ id: string; name: string }> }) => {
        if (cancelled) return;
        const pages = Array.isArray(j.pages) ? j.pages : [];
        setFbPages(pages);
        setFacebookPageId((prev) => {
          if (prev && pages.some((p) => p.id === prev)) return prev;
          return pages[0]?.id ?? '';
        });
      })
      .catch(() => {
        if (!cancelled) {
          setFbPages([]);
          setFacebookPageId('');
        }
      })
      .finally(() => {
        if (!cancelled) setFbPagesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, accounts?.facebook?.isConnected]);

  useEffect(() => {
    if (!open || !selected.has('pinterest')) {
      return;
    }
    let cancelled = false;
    void fetch('/api/pinterest/boards', { credentials: 'include' })
      .then((r) => r.json())
      .then((j: { boards?: Array<{ id: string; name: string }> }) => {
        if (!cancelled && Array.isArray(j.boards)) setPinterestBoards(j.boards);
      })
      .catch(() => {
        if (!cancelled) setPinterestBoards([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open, selected, accounts]);

  useEffect(() => {
    if (!open || !selected.has('pinterest') || pinterestBoardId) return;
    if (pinterestBoards.length === 1) setPinterestBoardId(pinterestBoards[0].id);
  }, [open, selected, pinterestBoardId, pinterestBoards]);

  useEffect(() => {
    if (!open || !selected.has('slack')) {
      return;
    }
    let cancelled = false;
    void fetch('/api/slack/conversations', { credentials: 'include' })
      .then((r) => r.json())
      .then((j: { channels?: Array<{ id: string; name: string }> }) => {
        if (!cancelled && Array.isArray(j.channels)) setSlackChannels(j.channels);
      })
      .catch(() => {
        if (!cancelled) setSlackChannels([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open, selected, accounts]);

  useEffect(() => {
    if (!open || !selected.has('slack') || slackChannelId) return;
    if (slackChannels.length === 1) setSlackChannelId(slackChannels[0].id);
  }, [open, selected, slackChannelId, slackChannels]);

  useEffect(() => {
    if (!open || !selected.has('discord')) {
      return;
    }
    let cancelled = false;
    void fetch('/api/discord/guild-channels', { credentials: 'include' })
      .then((r) => r.json())
      .then((j: { channels?: Array<{ id: string; name: string }> }) => {
        if (!cancelled && Array.isArray(j.channels)) setDiscordChannels(j.channels);
      })
      .catch(() => {
        if (!cancelled) setDiscordChannels([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open, selected, accounts]);

  useEffect(() => {
    if (!open || !selected.has('discord') || discordChannelId) return;
    if (discordChannels.length === 1) setDiscordChannelId(discordChannels[0].id);
  }, [open, selected, discordChannelId, discordChannels]);

  const captionTrimmed = caption.trim();
  const threadsBytes = threadsUtf8ByteLength(captionTrimmed);
  const threadsSelected = selected.has('threads');
  const threadsCaptionBytesOk =
    !threadsSelected || captionTrimmed.length === 0 || threadsBytes <= 500;
  const threadsPayloadOk =
    !threadsSelected ||
    (threadsCaptionBytesOk &&
      (canAttachImageToThreads(publishImageMedia) || captionTrimmed.length > 0));

  const instagramOk =
    !selected.has('instagram') ||
    Boolean(publishImageMedia && canPublishImageToInstagram(publishImageMedia));

  const facebookOk = !selected.has('facebook') || facebookPageId.trim().length > 0;

  const youtubeOk =
    !selected.has('youtube') ||
    (Boolean(publishImageMedia?.type.toLowerCase().startsWith('video/')) &&
      Boolean(youtubeTitle.trim()) &&
      Boolean(publishImageMedia?.url?.trim()));

  const linkedinOk =
    !selected.has('linkedin') ||
    (captionTrimmed.length > 0 && captionTrimmed.length <= 3000);
  const twitterCharCount = Array.from(captionTrimmed).length;
  const twitterExtrasNonEmpty = twitterThreadExtras.map((s) => s.trim()).filter(Boolean);
  const twitterUseThread = selected.has('twitter') && twitterExtrasNonEmpty.length > 0;
  const twitterThreadSegments = twitterUseThread
    ? [captionTrimmed, ...twitterExtrasNonEmpty]
    : [];
  const twitterThreadCharOk =
    !twitterUseThread ||
    (captionTrimmed.length > 0 &&
      twitterThreadSegments.every((s) => {
        const n = Array.from(s).length;
        return n > 0 && n <= 280;
      }));
  const twitterOk =
    !selected.has('twitter') ||
    (twitterUseThread
      ? twitterThreadCharOk
      : captionTrimmed.length > 0 && twitterCharCount > 0 && twitterCharCount <= 280);

  const publishExtraFields = useMemo(
    () => ({
      facebookPageId,
      youtubeTitle,
      pinterestBoardId,
      redditSubreddit,
      slackChannelId,
      discordChannelId,
    }),
    [facebookPageId, youtubeTitle, pinterestBoardId, redditSubreddit, slackChannelId, discordChannelId],
  );

  /** Drop selections that become ineligible (e.g. caption cleared) so rows never stay checked+disabled. */
  useEffect(() => {
    if (!open) return;
    setSelected((prev) => {
      if (prev.size === 0) return prev;
      const next = new Set<PublishV1Platform>();
      for (const p of prev) {
        if (
          computePublishEligibility({
            platform: p,
            imageUrl: mediaForSelector.url ?? '',
            mimeType: mediaForSelector.type ?? '',
            captionText: caption,
            forDefaultSelection: false,
            userPlan,
            ...publishExtraFields,
          }).eligible
        ) {
          next.add(p);
        }
      }
      if (next.size === prev.size && [...prev].every((x) => next.has(x))) return prev;
      return next;
    });
  }, [open, caption, mediaForSelector, publishExtraFields, userPlan]);

  const mastodonOk =
    !selected.has('mastodon') ||
    computePublishEligibility({
      platform: 'mastodon',
      imageUrl: mediaForSelector.url ?? '',
      mimeType: mediaForSelector.type ?? '',
      captionText: captionTrimmed,
      forDefaultSelection: false,
      ...publishExtraFields,
    }).eligible;

  const pinterestOk =
    !selected.has('pinterest') ||
    computePublishEligibility({
      platform: 'pinterest',
      imageUrl: mediaForSelector.url ?? '',
      mimeType: mediaForSelector.type ?? '',
      captionText: captionTrimmed,
      forDefaultSelection: false,
      ...publishExtraFields,
    }).eligible;

  const redditOk =
    !selected.has('reddit') ||
    computePublishEligibility({
      platform: 'reddit',
      imageUrl: mediaForSelector.url ?? '',
      mimeType: mediaForSelector.type ?? '',
      captionText: captionTrimmed,
      forDefaultSelection: false,
      ...publishExtraFields,
    }).eligible;

  const tiktokOk =
    !selected.has('tiktok') ||
    (computePublishEligibility({
      platform: 'tiktok',
      imageUrl: mediaForSelector.url ?? '',
      mimeType: mediaForSelector.type ?? '',
      captionText: captionTrimmed,
      forDefaultSelection: false,
      ...publishExtraFields,
    }).eligible &&
      tiktokOptionsValid &&
      Boolean(tiktokOptions.privacyLevel));

  const vimeoOk =
    !selected.has('vimeo') ||
    computePublishEligibility({
      platform: 'vimeo',
      imageUrl: mediaForSelector.url ?? '',
      mimeType: mediaForSelector.type ?? '',
      captionText: captionTrimmed,
      forDefaultSelection: false,
      ...publishExtraFields,
    }).eligible;

  const dribbbleOk =
    !selected.has('dribbble') ||
    computePublishEligibility({
      platform: 'dribbble',
      imageUrl: mediaForSelector.url ?? '',
      mimeType: mediaForSelector.type ?? '',
      captionText: captionTrimmed,
      forDefaultSelection: false,
      ...publishExtraFields,
    }).eligible;

  const snapchatOk =
    !selected.has('snapchat') ||
    computePublishEligibility({
      platform: 'snapchat',
      imageUrl: mediaForSelector.url ?? '',
      mimeType: mediaForSelector.type ?? '',
      captionText: captionTrimmed,
      forDefaultSelection: false,
      ...publishExtraFields,
    }).eligible;

  const slackOk =
    !selected.has('slack') ||
    computePublishEligibility({
      platform: 'slack',
      imageUrl: mediaForSelector.url ?? '',
      mimeType: mediaForSelector.type ?? '',
      captionText: captionTrimmed,
      forDefaultSelection: false,
      ...publishExtraFields,
    }).eligible;

  const discordOk =
    !selected.has('discord') ||
    computePublishEligibility({
      platform: 'discord',
      imageUrl: mediaForSelector.url ?? '',
      mimeType: mediaForSelector.type ?? '',
      captionText: captionTrimmed,
      forDefaultSelection: false,
      ...publishExtraFields,
    }).eligible;

  const publishSummary = useMemo(() => summarizePublishResults(results), [results]);
  const publishLocked = publishSummary.hasResults && publishSummary.anyOk && !publishing;

  const canSubmit =
    selected.size > 0 &&
    threadsPayloadOk &&
    instagramOk &&
    facebookOk &&
    youtubeOk &&
    linkedinOk &&
    twitterOk &&
    mastodonOk &&
    pinterestOk &&
    redditOk &&
    tiktokOk &&
    vimeoOk &&
    dribbbleOk &&
    snapchatOk &&
    slackOk &&
    discordOk &&
    (!selected.has('slack') || Boolean(slackChannelId.trim())) &&
    (!selected.has('discord') || Boolean(discordChannelId.trim())) &&
    !publishing &&
    !publishLocked &&
    !accountsPending;

  const publishExtra = useMemo(
    () => ({
      facebookPageId,
      youtubeTitle,
      pinterestBoardId,
      redditSubreddit,
      slackChannelId,
      discordChannelId,
    }),
    [facebookPageId, youtubeTitle, pinterestBoardId, redditSubreddit, slackChannelId, discordChannelId],
  );

  const handleClose = () => {
    if (publishing) {
      if (!window.confirm('Publishing is in progress. Stop and close?')) return;
    }
    abortRef.current?.abort();
    abortRef.current = null;
    setPublishing(false);
    onClose();
  };

  const handleConnectPlatform = useCallback(async (platform: PublishV1Platform) => {
    const r = await startOAuthConnect(platform);
    if (!r.ok) {
      window.alert(r.message);
    }
  }, []);

  const runPublish = async () => {
    if (!canSubmit) return;
    const ac = new AbortController();
    abortRef.current = ac;
    setPublishing(true);
    setResults(null);
    const out: PublishApiResult[] = [];
    try {
      for (const p of selected) {
        if (p === 'instagram') {
          if (!publishImageMedia) continue;
          out.push(
            await postInstagramPublish(
              buildInstagramNativePublishBody({
                imageUrl: toAbsoluteMediaUrl(publishImageMedia.url),
                caption: captionTrimmed,
              }),
              ac.signal,
            ),
          );
        } else if (p === 'threads') {
          const imageUrl =
            publishImageMedia && canAttachImageToThreads(publishImageMedia)
              ? toAbsoluteMediaUrl(publishImageMedia.url)
              : undefined;
          out.push(
            await postThreadsPublish(
              buildThreadsNativePublishBody({ imageUrl, caption: captionTrimmed }),
              ac.signal,
            ),
          );
        } else if (p === 'facebook' && facebookPageId.trim()) {
          if (publishImageMedia?.url?.trim()) {
            const abs = toAbsoluteMediaUrl(publishImageMedia.url);
            const isVid = publishImageMedia.type.toLowerCase().startsWith('video/');
            out.push(
              await postFacebookPublish(
                buildFacebookNativePublishBody({
                  pageId: facebookPageId.trim(),
                  imageUrl: abs,
                  caption: captionTrimmed,
                  mediaType: isVid ? 'video' : undefined,
                }),
                ac.signal,
              ),
            );
          } else {
            out.push(
              await postFacebookPublish(
                buildFacebookNativePublishBody({
                  pageId: facebookPageId.trim(),
                  message: captionTrimmed,
                }),
                ac.signal,
              ),
            );
          }
        } else if (p === 'youtube' && publishImageMedia?.url?.trim()) {
          out.push(
            await postYoutubePublish(
              buildYoutubeNativePublishBody({
                videoUrl: toAbsoluteMediaUrl(publishImageMedia.url),
                title: youtubeTitle.trim(),
                description: captionTrimmed || undefined,
                privacyStatus: youtubePrivacy,
                shorts: youtubeShorts || undefined,
              }),
              ac.signal,
            ),
          );
        } else if (p === 'linkedin' && captionTrimmed) {
          out.push(
            await postLinkedinPublish(buildLinkedinNativePublishBody({ caption: captionTrimmed }), ac.signal),
          );
        } else if (p === 'twitter' && captionTrimmed) {
          const extras = twitterThreadExtras.map((s) => s.trim()).filter(Boolean);
          if (extras.length > 0) {
            out.push(
              await postTwitterPublish(
                buildTwitterThreadPublishBody({ segments: [captionTrimmed, ...extras] }),
                ac.signal,
              ),
            );
          } else {
            out.push(
              await postTwitterPublish(buildTwitterNativePublishBody({ caption: captionTrimmed }), ac.signal),
            );
          }
        } else if (p === 'mastodon') {
          const img =
            publishImageMedia?.url?.trim() && publishImageMedia.type.toLowerCase().startsWith('image/')
              ? toAbsoluteMediaUrl(publishImageMedia.url)
              : undefined;
          out.push(
            await postMastodonPublish(
              buildMastodonNativePublishBody({ caption: captionTrimmed, imageUrl: img }),
              ac.signal,
            ),
          );
        } else if (p === 'pinterest' && pinterestBoardId.trim() && publishImageMedia?.url?.trim()) {
          out.push(
            await postPinterestPublish(
              buildPinterestNativePublishBody({
                boardId: pinterestBoardId.trim(),
                imageUrl: toAbsoluteMediaUrl(publishImageMedia.url),
                title: captionTrimmed.slice(0, 100) || contentTitle.trim().slice(0, 100) || undefined,
                description: captionTrimmed || undefined,
              }),
              ac.signal,
            ),
          );
        } else if (p === 'reddit' && redditSubreddit.trim()) {
          const rt =
            captionTrimmed.slice(0, 300) || contentTitle.trim().slice(0, 300) || 'Post';
          const hasMedia =
            Boolean(publishImageMedia?.url?.trim()) &&
            (publishImageMedia!.type.toLowerCase().startsWith('image/') ||
              publishImageMedia!.type.toLowerCase().startsWith('video/'));
          if (hasMedia) {
            out.push(
              await postRedditPublish(
                buildRedditNativePublishBody({
                  subreddit: redditSubreddit.trim(),
                  title: rt,
                  url: toAbsoluteMediaUrl(publishImageMedia!.url),
                }),
                ac.signal,
              ),
            );
          } else if (captionTrimmed) {
            out.push(
              await postRedditPublish(
                buildRedditNativePublishBody({
                  subreddit: redditSubreddit.trim(),
                  title: rt,
                  text: captionTrimmed,
                }),
                ac.signal,
              ),
            );
          }
        } else if (
          p === 'tiktok' &&
          publishImageMedia?.url?.trim() &&
          publishImageMedia.type.toLowerCase().startsWith('video/')
        ) {
          out.push(
            await postTiktokPublish(
              buildTikTokNativePublishBody({
                videoUrl: toAbsoluteMediaUrl(publishImageMedia.url),
                title: captionTrimmed.slice(0, 220) || contentTitle.trim().slice(0, 220) || undefined,
                privacyLevel: tiktokOptions.privacyLevel as
                  | 'PUBLIC_TO_EVERYONE'
                  | 'MUTUAL_FOLLOW_FRIENDS'
                  | 'FOLLOWER_OF_CREATOR'
                  | 'SELF_ONLY',
                allowComment: tiktokOptions.allowComment,
                allowDuet: tiktokOptions.allowDuet,
                allowStitch: tiktokOptions.allowStitch,
                brandOrganic: tiktokOptions.discloseCommercial && tiktokOptions.yourBrand,
                brandContent: tiktokOptions.discloseCommercial && tiktokOptions.brandedContent,
              }),
              ac.signal,
            ),
          );
        } else if (
          p === 'vimeo' &&
          publishImageMedia?.url?.trim() &&
          publishImageMedia.type.toLowerCase().startsWith('video/')
        ) {
          out.push(
            await postVimeoPublish(
              buildVimeoNativePublishBody({
                videoUrl: toAbsoluteMediaUrl(publishImageMedia.url),
                title: youtubeTitle.trim(),
                description: captionTrimmed || undefined,
              }),
              ac.signal,
            ),
          );
        } else if (p === 'dribbble' && publishImageMedia?.url?.trim()) {
          const shotTitle =
            captionTrimmed.slice(0, 75) || contentTitle.trim().slice(0, 75) || 'Shot';
          out.push(
            await postDribbblePublish(
              buildDribbbleNativePublishBody({
                imageUrl: toAbsoluteMediaUrl(publishImageMedia.url),
                title: shotTitle,
                description: captionTrimmed || undefined,
              }),
              ac.signal,
            ),
          );
        } else if (p === 'snapchat' && publishImageMedia?.url?.trim()) {
          const storyTitle =
            captionTrimmed.slice(0, 120) || contentTitle.trim().slice(0, 120) || undefined;
          out.push(
            await postSnapchatPublish(
              buildSnapchatNativePublishBody({
                imageUrl: toAbsoluteMediaUrl(publishImageMedia.url),
                title: storyTitle,
              }),
              ac.signal,
            ),
          );
        } else if (p === 'slack' && slackChannelId.trim()) {
          const hasMedia =
            Boolean(publishImageMedia?.url?.trim()) &&
            (publishImageMedia!.type.toLowerCase().startsWith('image/') ||
              publishImageMedia!.type.toLowerCase().startsWith('video/'));
          const body = buildSlackNativePublishBody({
            channelId: slackChannelId.trim(),
            caption: captionTrimmed,
            mediaUrl: hasMedia ? toAbsoluteMediaUrl(publishImageMedia!.url) : undefined,
          });
          if (body) {
            out.push(await postSlackPublish(body, ac.signal));
          }
        } else if (p === 'discord' && discordChannelId.trim()) {
          const hasMedia =
            Boolean(publishImageMedia?.url?.trim()) &&
            (publishImageMedia!.type.toLowerCase().startsWith('image/') ||
              publishImageMedia!.type.toLowerCase().startsWith('video/'));
          const body = buildDiscordNativePublishBody({
            channelId: discordChannelId.trim(),
            caption: captionTrimmed,
            mediaUrl: hasMedia ? toAbsoluteMediaUrl(publishImageMedia!.url) : undefined,
          });
          if (body) {
            out.push(await postDiscordPublish(body, ac.signal));
          }
        }
      }
      setResults(out);
      if (out.length > 0) {
        reportNativePublishDiagnostics(
          out.map((r) => ({
            platform: r.platform,
            ok: r.ok,
            detail: r.detail,
          })),
        );
      }
      const okIds = out.filter((r) => r.ok).map((r) => r.platform);
      if (okIds.length > 0) {
        void fetch('/api/workspace/publish-notify', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ platforms: okIds }),
        }).catch(() => {});
      }
    } catch (e) {
      if (!(e instanceof DOMException && e.name === 'AbortError')) {
        const first = [...selected][0] ?? 'instagram';
        setResults([
          {
            platform: first,
            ok: false,
            httpStatus: 0,
            detail: e instanceof Error ? e.message : 'Network error while publishing.',
          },
        ]);
      }
    } finally {
      setPublishing(false);
      abortRef.current = null;
    }
  };

  const failedPlatforms = useMemo(
    () => results?.filter((r) => !r.ok).map((r) => r.platform) ?? [],
    [results],
  );

  const retryFailed = () => {
    const allowed = getDefaultPublishSelection(
      accounts,
      mediaForSelector,
      captionTrimmed,
      {
        facebookPageId,
        youtubeTitle,
        pinterestBoardId,
        redditSubreddit,
        slackChannelId,
        discordChannelId,
      },
      userPlan,
    );
    setSelected(new Set(failedPlatforms.filter((p) => allowed.has(p))));
    setResults(null);
  };

  /** Phase MB.10 */
  useEffect(() => {
    if (!results?.length || publishing) return;
    if (!results.some((r) => r.ok)) return;
    try {
      if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
        navigator.vibrate(12);
      }
    } catch {
      /* ignore */
    }
  }, [results, publishing]);

  useEffect(() => {
    if (!results?.length || publishing) return;
    const t = window.setTimeout(() => {
      publishResultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 80);
    return () => window.clearTimeout(t);
  }, [results, publishing]);

  const hint = useMemo(() => {
    const parts: string[] = [];
    for (const p of selected) {
      const h = PUBLISH_V1_PAYLOAD_HINTS[p as keyof typeof PUBLISH_V1_PAYLOAD_HINTS];
      if (h) parts.push(h);
    }
    return parts.join(' ');
  }, [selected]);

  const imageHttpsNote =
    publishImageMedia &&
    publishImageMedia.type.startsWith('image/') &&
    !isPublicHttpsMediaUrl(publishImageMedia.url)
      ? 'This library image does not resolve to a public HTTPS URL Meta can fetch. Pick another asset or upload to a public URL.'
      : null;

  const videoHttpsNote =
    publishImageMedia &&
    publishImageMedia.type.startsWith('video/') &&
    !isPublicHttpsMediaUrl(publishImageMedia.url)
      ? 'This library video does not resolve to a public HTTPS URL. YouTube, Facebook, TikTok, and Vimeo need a URL the server can fetch.'
      : null;

  const showSharedVideoTitle = selected.has('youtube') || selected.has('vimeo');
  const videoTitleFieldLabel =
    selected.has('youtube') && selected.has('vimeo')
      ? 'Video title (YouTube & Vimeo)'
      : selected.has('vimeo')
        ? 'Vimeo title'
        : 'YouTube title';

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (canSubmit) void runPublish();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen={fullScreen}
      maxWidth="sm"
      fullWidth
      scroll="paper"
      disableAutoFocus
      sx={{ zIndex: 1700 }}
      aria-labelledby="create-publish-dialog-title"
      PaperProps={{
        component: 'form' as const,
        onSubmit: handleFormSubmit,
        sx: {
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          ...(fullScreen
            ? {
                m: 0,
                maxHeight: '100dvh',
                height: '100%',
                borderRadius: 0,
                pt: 'max(8px, env(safe-area-inset-top, 0px))',
              }
            : {
                maxHeight: 'min(90dvh, 640px)',
              }),
        },
      }}
    >
      <DialogTitle
        id="create-publish-dialog-title"
        sx={{
          flexShrink: 0,
          pr: 1,
          pl: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexWrap: 'wrap',
        }}
      >
        <Typography variant="h6" component="span" sx={{ flex: '1 1 auto', minWidth: 0 }} noWrap>
          Post now
        </Typography>
        {fullScreen ? (
          <FormControlLabel
            sx={{ m: 0, flexShrink: 0 }}
            control={
              <Switch
                size="small"
                checked={compactView}
                onChange={(e) => setCompactViewPersisted(e.target.checked)}
                inputProps={{ 'aria-label': 'Compact publish view' }}
              />
            }
            label={<Typography variant="caption">Compact</Typography>}
          />
        ) : null}
        <IconButton
          aria-label="Close publish dialog"
          onClick={handleClose}
          edge="end"
          sx={{ flexShrink: 0, minWidth: 44, minHeight: 44 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        dividers
        sx={{
          flex: '1 1 auto',
          overflowY: 'auto',
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {compactView ? (
          <Accordion defaultExpanded={false} sx={{ mb: 2, '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2">About native publish</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" color="text.secondary">
                Sends to selected networks via FloAI native routes (Meta, Facebook Page, YouTube video, etc.). Save &amp;
                Schedule uses Buffer — not these APIs. Connect Google on Profile for Calendar only.
              </Typography>
            </AccordionDetails>
          </Accordion>
        ) : (
          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>Post now</strong> sends to the selected networks using FloAI&apos;s native publish routes (Meta,
            <strong> Facebook Page</strong> — not personal profile — and <strong>YouTube</strong> video upload).
            Connect <strong>Google</strong> on your Profile for <strong>Calendar</strong>; it is not a publish target
            here. <strong>Save &amp; Schedule</strong> still stores your draft and may enqueue the existing{' '}
            <strong>Buffer</strong> job — it does <strong>not</strong> call these native publish APIs.
          </Alert>
        )}
        <PublishOutcomeBanner results={results} publishing={publishing} />
        {publishing ? (
          <LinearProgress
            sx={{
              alignSelf: 'stretch',
              flexShrink: 0,
              position: 'sticky',
              top: 0,
              zIndex: 2,
              mb: 2,
              bgcolor: 'background.paper',
            }}
            aria-label="Publishing in progress"
          />
        ) : null}
        {accountsPending ? (
          <Typography variant="body2" color="text.secondary">
            Loading connected accounts…
          </Typography>
        ) : (
          <>
            {accounts?.facebook?.isConnected ? (
              <Box sx={{ mb: 2 }}>
                {fbPagesLoading ? (
                  <Typography variant="body2" color="text.secondary">
                    Loading Facebook Pages…
                  </Typography>
                ) : fbPages.length === 0 ? (
                  <Alert severity="warning" variant="outlined">
                    No Facebook Pages returned. Reconnect with Page permissions — letsgo/docs/plans/completed/floai_meta_native_publish_phase2.md.
                  </Alert>
                ) : (
                  <FormControl fullWidth disabled={publishing}>
                    <InputLabel id="create-publish-fb-page-label">Facebook Page (not personal profile)</InputLabel>
                    <Select
                      MenuProps={PUBLISH_DIALOG_SELECT_MENU_PROPS}
                      labelId="create-publish-fb-page-label"
                      label="Facebook Page (not personal profile)"
                      value={facebookPageId}
                      onChange={(e) => setFacebookPageId(String(e.target.value))}
                    >
                      {fbPages.map((p) => (
                        <MenuItem key={p.id} value={p.id}>
                          {p.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Box>
            ) : null}
            <PublishPlatformSelector
              accounts={accounts}
              media={mediaForSelector}
              captionText={caption}
              publishExtra={publishExtra}
              selected={selected}
              onChangeSelected={setSelected}
              compact={compactView}
              destinationPicker
              onConnectPlatform={handleConnectPlatform}
              userPlan={userPlan}
            />
          </>
        )}
        {selected.has('facebook') && accounts?.facebook?.isConnected && fbPages.length > 0 && !facebookPageId ? (
          <Alert severity="warning" variant="outlined" sx={{ mt: 2 }}>
            Choose a Facebook Page above before publishing.
          </Alert>
        ) : null}
        {showSharedVideoTitle ? (
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              id="create-publish-yt-title"
              label={videoTitleFieldLabel}
              value={youtubeTitle}
              onChange={(e) => setYoutubeTitle(e.target.value.slice(0, 100))}
              fullWidth
              required
              disabled={publishing}
              helperText="Required for YouTube / Vimeo when selected. Defaults from your content title when you open this dialog."
            />
            {selected.has('youtube') ? (
              <>
                <FormControl fullWidth disabled={publishing}>
                  <InputLabel id="create-publish-yt-privacy-label">YouTube privacy</InputLabel>
                  <Select
                      MenuProps={PUBLISH_DIALOG_SELECT_MENU_PROPS}
                    labelId="create-publish-yt-privacy-label"
                    label="YouTube privacy"
                    value={youtubePrivacy}
                    onChange={(e) =>
                      setYoutubePrivacy(e.target.value as 'public' | 'unlisted' | 'private')
                    }
                  >
                    <MenuItem value="public">Public</MenuItem>
                    <MenuItem value="unlisted">Unlisted</MenuItem>
                    <MenuItem value="private">Private</MenuItem>
                  </Select>
                </FormControl>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={youtubeShorts}
                      onChange={(e) => setYoutubeShorts(e.target.checked)}
                      disabled={publishing}
                    />
                  }
                  label="Shorts — append #Shorts to title (vertical ≤60s videos per YouTube)"
                />
              </>
            ) : null}
          </Box>
        ) : null}
        {selected.has('tiktok') ? (
          <TikTokPublishOptionsPanel
            disabled={publishing}
            value={tiktokOptions}
            onChange={setTiktokOptions}
            onValidityChange={setTiktokOptionsValid}
          />
        ) : null}
        {selected.has('pinterest') ? (
          <Box sx={{ mt: 2 }}>
            {pinterestBoards.length === 0 ? (
              <Alert severity="warning" variant="outlined">
                No Pinterest boards returned. Reconnect Pinterest with boards:read and pins:write, or create a board in
                Pinterest first.
              </Alert>
            ) : (
              <FormControl fullWidth disabled={publishing}>
                <InputLabel id="create-publish-pinterest-board-label">Pinterest board</InputLabel>
                <Select
                      MenuProps={PUBLISH_DIALOG_SELECT_MENU_PROPS}
                  labelId="create-publish-pinterest-board-label"
                  label="Pinterest board"
                  value={pinterestBoardId}
                  onChange={(e) => setPinterestBoardId(String(e.target.value))}
                >
                  {pinterestBoards.map((b) => (
                    <MenuItem key={b.id} value={b.id}>
                      {b.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        ) : null}
        {selected.has('reddit') ? (
          <Box sx={{ mt: 2 }}>
            <TextField
              id="create-publish-reddit-sub"
              label="Subreddit (no r/ prefix)"
              value={redditSubreddit}
              onChange={(e) => setRedditSubreddit(e.target.value)}
              fullWidth
              required
              disabled={publishing}
              placeholder="e.g. test"
              helperText="Link post if you picked an image or video above; otherwise the caption becomes the Reddit post body."
            />
          </Box>
        ) : null}
        {selected.has('slack') ? (
          <Box sx={{ mt: 2 }}>
            {slackChannels.length === 0 ? (
              <Alert severity="warning" variant="outlined">
                No Slack channels returned. Reconnect with <strong>channels:read</strong> and <strong>chat:write</strong>.
              </Alert>
            ) : (
              <FormControl fullWidth disabled={publishing}>
                <InputLabel id="create-publish-slack-channel-label">Slack channel</InputLabel>
                <Select
                      MenuProps={PUBLISH_DIALOG_SELECT_MENU_PROPS}
                  labelId="create-publish-slack-channel-label"
                  label="Slack channel"
                  value={slackChannelId}
                  onChange={(e) => setSlackChannelId(String(e.target.value))}
                >
                  {slackChannels.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        ) : null}
        {selected.has('discord') ? (
          <Box sx={{ mt: 2 }}>
            {discordChannels.length === 0 ? (
              <Alert severity="warning" variant="outlined">
                No Discord channels returned. Reconnect Discord and add the FloAI bot to a server; deployment needs{' '}
                <strong>DISCORD_BOT_TOKEN</strong>.
              </Alert>
            ) : (
              <FormControl fullWidth disabled={publishing}>
                <InputLabel id="create-publish-discord-channel-label">Discord channel</InputLabel>
                <Select
                      MenuProps={PUBLISH_DIALOG_SELECT_MENU_PROPS}
                  labelId="create-publish-discord-channel-label"
                  label="Discord channel"
                  value={discordChannelId}
                  onChange={(e) => setDiscordChannelId(String(e.target.value))}
                >
                  {discordChannels.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        ) : null}
        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Caption defaults to your title + plain text from the editor (HTML stripped for Threads). Edit before
            publishing.
          </Typography>
          <Button variant="outlined" size="small" onClick={onRequestPickImage} sx={{ alignSelf: 'flex-start' }}>
            {publishImageMedia ? 'Change library image or video…' : 'Choose library image or video…'}
          </Button>
          {publishImageMedia ? (
            <Typography variant="caption" color="text.secondary">
              Selected: {publishImageMedia.altText || publishImageMedia.id} —{' '}
              <Button size="small" onClick={() => onPublishImageMediaChange(null)}>
                Clear asset
              </Button>
            </Typography>
          ) : null}
          {imageHttpsNote ? <Alert severity="warning">{imageHttpsNote}</Alert> : null}
          {videoHttpsNote ? <Alert severity="warning">{videoHttpsNote}</Alert> : null}
        </Box>
        <ThreadsCaptionByteCounter visible={threadsSelected && showThreadsByteChip} bytes={threadsBytes} />
        <TextField
          id="create-publish-caption"
          label="Caption / post text"
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
              e.preventDefault();
              if (canSubmit) void runPublish();
            }
          }}
          fullWidth
          multiline
          minRows={4}
          sx={{ mt: 2 }}
          disabled={publishing}
          autoFocus
          inputProps={{ 'aria-describedby': 'create-publish-caption-helper' }}
          error={Boolean(
            (threadsSelected && captionTrimmed.length > 0 && threadsBytes > 500) ||
              (selected.has('twitter') &&
                (twitterUseThread
                  ? twitterThreadSegments.some((s) => Array.from(s).length > 280)
                  : twitterCharCount > 280)),
          )}
          FormHelperTextProps={{ id: 'create-publish-caption-helper' }}
          helperText={
            (threadsSelected
              ? `Threads UTF-8 size (when sent as post text): ${threadsBytes} / 500`
              : selected.has('twitter')
                ? twitterUseThread
                  ? `X thread: ${twitterThreadSegments.length} tweet(s); each must be 1–280 characters.`
                  : `X character count: ${twitterCharCount} / 280`
                : selected.has('linkedin')
                  ? `LinkedIn uses this caption as the full post text (${captionTrimmed.length} / 3000 characters).`
                  : 'Instagram / Facebook: optional caption. YouTube: description. Threads / X / LinkedIn: post text as above.') +
            ' Ctrl+Enter (⌘+Enter) publishes.'
          }
        />
        {selected.has('twitter') ? (
          <Box sx={{ mt: 2 }}>
            <Button
              type="button"
              variant="outlined"
              size="small"
              disabled={publishing || twitterThreadExtras.length >= 7}
              onClick={() => setTwitterThreadExtras((prev) => [...prev, ''])}
            >
              Add thread reply ({twitterThreadExtras.length}/7)
            </Button>
            {twitterThreadExtras.map((line, idx) => (
              <TextField
                key={idx}
                id={`create-publish-x-thread-${idx}`}
                label={`X reply ${idx + 2}`}
                value={line}
                onChange={(e) => {
                  const v = e.target.value;
                  setTwitterThreadExtras((prev) => {
                    const next = [...prev];
                    next[idx] = v;
                    return next;
                  });
                }}
                fullWidth
                multiline
                minRows={2}
                sx={{ mt: 1.5 }}
                disabled={publishing}
                helperText={`${Array.from(line.trim()).length} / 280`}
              />
            ))}
          </Box>
        ) : null}
        {hint && !compactView ? (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            {hint}
          </Typography>
        ) : null}
        {hint && compactView ? (
          <Accordion defaultExpanded={false} sx={{ mt: 1.5, '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2">Platform tips</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="caption" color="text.secondary" component="div">
                {hint}
              </Typography>
            </AccordionDetails>
          </Accordion>
        ) : null}
        {results && results.length > 0 ? (
          <Box ref={publishResultsRef} id="publish-results-anchor" sx={{ scrollMarginTop: 12 }}>
            <PublishMultiResults
              results={results}
              failedPlatforms={failedPlatforms}
              onRetryFailed={retryFailed}
              compact={compactResults}
            />
          </Box>
        ) : null}
      </DialogContent>
      <DialogActions
        sx={{
          flexShrink: 0,
          px: 2,
          py: 1.5,
          gap: 1,
          flexWrap: 'wrap',
          flexDirection: { xs: 'column-reverse', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          justifyContent: { xs: 'stretch', sm: 'flex-end' },
          borderTop: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          pb: `calc(12px + env(safe-area-inset-bottom, 0px) + ${keyboardBottomInset}px)`,
        }}
      >
        <Button onClick={handleClose} color="inherit" sx={{ minHeight: 44 }}>
          {publishing ? 'Cancel' : publishSummary.allOk ? 'Done' : 'Close'}
        </Button>
        {publishSummary.allOk && !publishing ? (
          <Button
            type="button"
            variant="contained"
            onClick={handleClose}
            sx={{ minHeight: 48, minWidth: { xs: '100%', sm: 140 } }}
          >
            Done
          </Button>
        ) : (
          <Button
            type="submit"
            variant="contained"
            disabled={!canSubmit}
            aria-busy={publishing}
            sx={{ minHeight: 48, minWidth: { xs: '100%', sm: 140 } }}
          >
            Publish
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
